<script setup>
// TODO This is a huge component, we should split this up into separate composables
// like `useSignature`, `useImageHandling`, `useFileUpload`, `useSpecialContent``
import {
  ref,
  unref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  useTemplateRef,
  nextTick,
} from 'vue';

import CannedResponse from '../conversation/CannedResponse.vue';
import KeyboardEmojiSelector from './keyboardEmojiSelector.vue';
import TagAgents from '../conversation/TagAgents.vue';
import VariableList from '../conversation/VariableList.vue';
import MacroList from '../conversation/MacroList.vue';

import { useEmitter } from 'dashboard/composables/emitter';
import { useI18n } from 'vue-i18n';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { lift, setBlockType, wrapIn } from 'prosemirror-commands';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import {
  messageSchema,
  buildMessageSchema,
  buildEditor,
  EditorView,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  EditorState,
  Selection,
  imageResizeView,
} from '@chatwoot/prosemirror-schema';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';

import {
  appendSignature,
  collapseSelection,
  findNodeToInsertImage,
  getContentNode,
  insertAtCursor,
  removeSignature as removeSignatureHelper,
  scrollCursorIntoView,
  getFormattingForEditor,
  getEffectiveChannelType,
  stripUnsupportedFormatting,
  createVariableInputRule,
} from 'dashboard/helper/editorHelper';
import {
  hasPressedEnterAndNotCmdOrShift,
  hasPressedCommandAndEnter,
  isComposing,
  isEscape,
} from 'shared/helpers/KeyboardHelpers';
import { createTypingIndicator } from '@chatwoot/utils';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  modelValue: { type: String, default: '' },
  editorId: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  isPrivate: { type: Boolean, default: false },
  enableSuggestions: { type: Boolean, default: true },
  overrideLineBreaks: { type: Boolean, default: false },
  updateSelectionWith: { type: String, default: '' },
  enableVariables: { type: Boolean, default: false },
  enableCannedResponses: { type: Boolean, default: true },
  enableMacros: { type: Boolean, default: false },
  variables: { type: Object, default: () => ({}) },
  signature: { type: String, default: '' },
  // allowSignature is a kill switch, ensuring no signature methods
  // are triggered except when this flag is true
  allowSignature: { type: Boolean, default: false },
  channelType: { type: String, default: '' },
  medium: { type: String, default: '' },
  focusOnMount: { type: Boolean, default: true },
  showSourceToggle: { type: Boolean, default: false },
  // Global INSERT_INTO_RICH_EDITOR bus events (Copilot "Use this", article
  // links) are meant for the conversation reply editor only — other mounted
  // editors (canned responses, signature, etc.) must not consume them.
  enableInsertEvents: { type: Boolean, default: false },
});

const emit = defineEmits([
  'typingOn',
  'typingOff',
  'toggleUserMention',
  'toggleCannedMenu',
  'toggleVariablesMenu',
  'toggleMacrosMenu',
  'executeMacro',
  'clearSelection',
  'blur',
  'focus',
  'input',
  'update:modelValue',
  'insertCannedResponse',
]);

const { t } = useI18n();

const TYPING_INDICATOR_IDLE_TIME = 4000;
const MAXIMUM_FILE_UPLOAD_SIZE = 4; // in MB
const DEFAULT_FORMATTING = 'Context::Default';
const PRIVATE_NOTE_FORMATTING = 'Context::PrivateNote';
const MESSAGE_SIGNATURE_FORMATTING = 'Context::MessageSignature';
const INLINE_CODE_TITLE = 'Toggle code font';
const CODE_BLOCK_BUTTON_CLASS = 'ProseMirror-code-block-button';
const BLOCKQUOTE_BUTTON_CLASS = 'ProseMirror-blockquote-button';
const INLINE_IMAGE_PASTE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

const effectiveChannelType = computed(() =>
  getEffectiveChannelType(props.channelType, props.medium)
);

const editorSchema = computed(() => {
  if (!props.channelType) return messageSchema;

  const formatType = props.isPrivate
    ? PRIVATE_NOTE_FORMATTING
    : effectiveChannelType.value;
  const formatting = getFormattingForEditor(formatType);
  return buildMessageSchema(formatting.marks, formatting.nodes);
});

const editorMenuOptions = computed(() => {
  const formatType = props.isPrivate
    ? PRIVATE_NOTE_FORMATTING
    : effectiveChannelType.value || DEFAULT_FORMATTING;
  const formatting = getFormattingForEditor(formatType);

  return formatting.menu;
});

const createState = (content, placeholder, plugins = [], methods = {}) => {
  const schema = editorSchema.value;
  // Strip unsupported formatting before parsing to prevent "Token type not supported" errors
  const sanitizedContent = stripUnsupportedFormatting(content, schema);
  return EditorState.create({
    doc: new MessageMarkdownTransformer(schema).parse(sanitizedContent),
    plugins: buildEditor({
      schema,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions: editorMenuOptions.value,
      resizableTableColumns: false,
    }),
  });
};

const { isEditorHotKeyEnabled, fetchSignatureFlagFromUISettings } =
  useUISettings();

const typingIndicator = createTypingIndicator(
  () => emit('typingOn'),
  () => emit('typingOff'),
  TYPING_INDICATOR_IDLE_TIME
);

// we don't need them to be reactive
// It cases weird issues where the objects are proxied
// and then the editor doesn't work as expected
// We have to wrap them in closures or use toRaw to get the actual values
let editorView = null;
let state = null;
let menuObserver = null;
let promptObserver = null;
let isApplyingFenceTransform = false;

const showUserMentions = ref(false);
const showCannedMenu = ref(false);
const showVariables = ref(false);
const showEmojiMenu = ref(false);
const showMacroMenu = ref(false);
const mentionSearchKey = ref('');
const cannedSearchTerm = ref('');
const variableSearchTerm = ref('');
const emojiSearchTerm = ref('');
const macroSearchTerm = ref('');
const range = ref(null);

// Source mode toggle state
const isSourceMode = ref(false);
const sourceContent = ref('');

// element ref
const editorRoot = useTemplateRef('editorRoot');
const imageUpload = useTemplateRef('imageUpload');
const editor = useTemplateRef('editor');

const supportsCodeBlock = computed(() => {
  return (
    editorMenuOptions.value.includes('codeBlock') &&
    !!editorSchema.value.nodes.code_block
  );
});

const supportsBlockquote = computed(() => {
  return (
    editorMenuOptions.value.includes('blockquote') &&
    !!editorSchema.value.nodes.blockquote
  );
});

const safeT = (key, fallback) => {
  try {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const res = t(key);
    return res && res !== key ? res : fallback;
  } catch {
    return fallback;
  }
};

const codeInlineTooltip = () => safeT('EDITOR.FORMAT_BAR.CODE', 'Inline code');

const codeBlockTooltip = () =>
  safeT('EDITOR.FORMAT_BAR.CODE_BLOCK', 'Code block');

const blockquoteTooltip = () => safeT('EDITOR.FORMAT_BAR.QUOTE', 'Quote');

const isSelectionInsideCodeBlock = () => {
  if (!editorView?.state?.schema?.nodes?.code_block) return false;
  const { $from } = editorView.state.selection;
  return $from.parent.type === editorView.state.schema.nodes.code_block;
};

const isSelectionInsideBlockquote = () => {
  if (!editorView?.state?.schema?.nodes?.blockquote) return false;
  const {
    state: {
      schema: {
        nodes: { blockquote },
      },
      selection: { $from },
    },
  } = editorView;

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type === blockquote) return true;
  }
  return false;
};

const selectedTextForCodeBlock = (doc, from, to) => {
  return doc.textBetween(from, to, '\n', '\n').replace(/\n$/, '');
};

const createCodeBlockNode = (schema, codeBlock, text) => {
  return codeBlock.create(null, text ? schema.text(text) : null);
};

const findNodePositionAfter = (doc, nodeType, startPosition) => {
  let nodePosition = null;
  doc.descendants((node, pos) => {
    if (nodePosition !== null) return false;
    if (pos >= startPosition && node.type === nodeType) {
      nodePosition = pos;
      return false;
    }
    return true;
  });
  return nodePosition;
};

const codeTextFromFence = text => {
  return text.startsWith('\n') ? text.slice(1) : text;
};

const textBeforePositionWithMap = (doc, to) => {
  let text = '';
  const positions = [];

  doc.descendants((node, pos) => {
    if (pos >= to) return false;

    if (node.isBlock && pos > 0 && text && !text.endsWith('\n')) {
      text += '\n';
      positions.push(pos);
    }

    if (node.isText) {
      const length = Math.min(node.text.length, to - pos);
      for (let index = 0; index < length; index += 1) {
        text += node.text[index];
        positions.push(pos + index);
      }
      return false;
    }

    if (node.type.name === 'hard_break') {
      text += '\n';
      positions.push(pos);
      return false;
    }

    return true;
  });

  return { text, positions };
};

const pendingCodeFenceContext = editorState => {
  const { selection } = editorState;
  const { from } = selection;
  const { text: textBeforeCursor, positions } = textBeforePositionWithMap(
    editorState.doc,
    from
  );
  const openingFenceIndex = textBeforeCursor.lastIndexOf('```');
  if (openingFenceIndex === -1) return null;

  const isOpeningAtLineStart =
    openingFenceIndex === 0 || textBeforeCursor[openingFenceIndex - 1] === '\n';
  if (!isOpeningAtLineStart) return null;

  return {
    openingFenceIndex,
    openingFencePosition: positions[openingFenceIndex],
    textBeforeCursor,
  };
};

const closeCodeBlockFence = (text, event) => {
  if (text !== '`' || !editorView) return false;
  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock, paragraph },
    },
    selection,
  } = editorState;

  if (!codeBlock || !paragraph || selection.empty === false) return false;

  const { $from, from } = selection;
  if ($from.parent.type !== codeBlock) return false;

  const before = editorState.doc.textBetween(
    Math.max($from.start(), from - 2),
    from,
    '',
    ''
  );
  if (before !== '``') return false;

  const codeBlockEnd = $from.after();
  let transaction = editorState.tr.delete(from - 2, from);
  const paragraphPosition = transaction.mapping.map(codeBlockEnd);

  transaction = transaction
    .insert(paragraphPosition, paragraph.create())
    .setSelection(
      Selection.near(transaction.doc.resolve(paragraphPosition + 1))
    )
    .scrollIntoView();

  event.preventDefault();
  editorView.dispatch(transaction);
  return true;
};

const convertTextFenceToCodeBlock = (text, event) => {
  if (text !== '`' || !editorView) return false;
  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock, paragraph },
    },
    selection,
  } = editorState;

  if (!codeBlock || !paragraph || selection.empty === false) return false;

  const { $from, from } = selection;
  if ($from.parent.type === codeBlock) return false;

  const fenceContext = pendingCodeFenceContext(editorState);
  if (!fenceContext?.textBeforeCursor.endsWith('``')) return false;

  const codeText = codeTextFromFence(
    fenceContext.textBeforeCursor
      .slice(0, -2)
      .slice(fenceContext.openingFenceIndex + 3)
  );
  if (!codeText) return false;

  const codeBlockNode = createCodeBlockNode(
    editorState.schema,
    codeBlock,
    codeText
  );

  let transaction = editorState.tr.replaceRangeWith(
    fenceContext.openingFencePosition,
    from,
    codeBlockNode
  );
  const codeBlockPosition = findNodePositionAfter(
    transaction.doc,
    codeBlock,
    transaction.mapping.map(fenceContext.openingFencePosition, -1)
  );
  if (codeBlockPosition === null) return false;
  const paragraphPosition = codeBlockPosition + codeBlockNode.nodeSize;

  transaction = transaction
    .insert(paragraphPosition, paragraph.create())
    .setSelection(
      Selection.near(transaction.doc.resolve(paragraphPosition + 1))
    )
    .scrollIntoView();

  event.preventDefault();
  editorView.dispatch(transaction);
  return true;
};

const keepOpeningFenceAsText = (text, event) => {
  if (text !== '`' || !editorView) return false;
  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock },
    },
    selection,
  } = editorState;

  if (!codeBlock || selection.empty === false) return false;

  const { $from, from } = selection;
  if ($from.parent.type === codeBlock) return false;

  const { text: textBeforeCursor } = textBeforePositionWithMap(
    editorState.doc,
    from
  );
  if (!textBeforeCursor.endsWith('``')) return false;

  const isFenceAtLineStart =
    textBeforeCursor === '``' || textBeforeCursor.endsWith('\n``');
  if (!isFenceAtLineStart) return false;

  event.preventDefault();
  editorView.dispatch(editorState.tr.insertText('`', from, from));
  return true;
};

const insertPendingFenceText = (text, event) => {
  if (!text || !editorView) return false;

  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock },
    },
    selection,
  } = editorState;

  if (!codeBlock || selection.empty === false) return false;

  const { $from, from } = selection;
  if ($from.parent.type === codeBlock) return false;
  if (!pendingCodeFenceContext(editorState)) return false;

  event.preventDefault();
  editorView.dispatch(editorState.tr.insertText(text, from, from));
  return true;
};

const convertOpeningGreaterThanToBlockquote = (text, event) => {
  if (text !== '>' || !editorView || !supportsBlockquote.value) return false;

  const {
    state: editorState,
    state: {
      schema: {
        nodes: { blockquote, code_block: codeBlock },
      },
      selection,
    },
  } = editorView;

  if (!blockquote || selection.empty === false) return false;
  if (codeBlock && selection.$from.parent.type === codeBlock) return false;
  if (selection.$from.parentOffset !== 0) return false;
  if (selection.$from.parent.content.size > 0) return false;

  let applied = false;
  event.preventDefault();
  wrapIn(blockquote)(editorState, transaction => {
    applied = true;
    editorView.dispatch(transaction.scrollIntoView());
  });
  return applied;
};

const convertInlineCodeOnClosingTick = (text, event) => {
  if (text !== '`' || !editorView) return false;

  const { state: editorState } = editorView;
  const {
    schema: {
      marks: { code: codeMarkType },
      nodes: { code_block: codeBlock },
    },
    selection,
  } = editorState;

  if (!codeMarkType || selection.empty === false) return false;

  const { $from, from } = selection;
  if (codeBlock && $from.parent.type === codeBlock) return false;

  const { text: textBeforeCursor, positions } = textBeforePositionWithMap(
    editorState.doc,
    from
  );
  if (!textBeforeCursor || textBeforeCursor.endsWith('``')) return false;

  const openingTickIndex = textBeforeCursor.lastIndexOf('`');
  if (openingTickIndex === -1) return false;

  const codeText = textBeforeCursor.slice(openingTickIndex + 1);
  if (!codeText || codeText.includes('\n')) return false;
  if (/^\s|\s$/.test(codeText)) return false;

  const openingTickPosition = positions[openingTickIndex];
  if (openingTickPosition === undefined) return false;

  const codeTextNode = editorState.schema.text(codeText, [
    codeMarkType.create(),
  ]);
  const trailingSpaceNode = editorState.schema.text(' ');
  const trailingSpacePosition = openingTickPosition + codeText.length;
  const cursorPosition = trailingSpacePosition + trailingSpaceNode.nodeSize;
  let transaction = editorState.tr
    .replaceRangeWith(openingTickPosition, from, codeTextNode)
    .insert(trailingSpacePosition, trailingSpaceNode)
    .setStoredMarks(null);

  transaction = transaction
    .setSelection(selection.constructor.create(transaction.doc, cursorPosition))
    .scrollIntoView();

  event.preventDefault();
  editorView.dispatch(transaction);
  return true;
};

const handlePendingFenceTextInput = event => {
  if (event.inputType !== 'insertText') return false;
  const text = event.data;

  return (
    closeCodeBlockFence(text, event) ||
    convertTextFenceToCodeBlock(text, event) ||
    keepOpeningFenceAsText(text, event) ||
    convertOpeningGreaterThanToBlockquote(text, event) ||
    convertInlineCodeOnClosingTick(text, event) ||
    insertPendingFenceText(text, event)
  );
};

const insertPendingFenceLineBreak = event => {
  if (!editorView || event.altKey) return false;

  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock, hard_break: hardBreak },
    },
    selection,
  } = editorState;

  if (!codeBlock || selection.empty === false) return false;

  const { $from } = selection;
  if ($from.parent.type === codeBlock) return false;
  if (!pendingCodeFenceContext(editorState)) return false;

  if (
    event.key === 'Enter' &&
    (event.ctrlKey || event.metaKey || event.shiftKey)
  ) {
    if (!hardBreak) return false;
    event.preventDefault();
    editorView.dispatch(
      editorState.tr.replaceSelectionWith(hardBreak.create())
    );
    return true;
  }
  return false;
};

const transformCompletedFenceInEditor = () => {
  if (!editorView || isApplyingFenceTransform) return false;

  const { state: editorState } = editorView;
  const {
    schema: {
      nodes: { code_block: codeBlock, paragraph },
    },
    selection,
  } = editorState;

  if (!codeBlock || !paragraph || selection.empty === false) return false;

  const { $from, from } = selection;
  if ($from.parent.type === codeBlock) return false;

  const { text: textBeforeCursor, positions } = textBeforePositionWithMap(
    editorState.doc,
    from
  );
  if (!textBeforeCursor.endsWith('```')) return false;

  const textBeforeClosingFence = textBeforeCursor.slice(0, -3);
  const openingFenceIndex = textBeforeClosingFence.lastIndexOf('```');
  if (openingFenceIndex === -1) return false;

  const isOpeningAtLineStart =
    openingFenceIndex === 0 ||
    textBeforeClosingFence[openingFenceIndex - 1] === '\n';
  if (!isOpeningAtLineStart) return false;

  const codeText = codeTextFromFence(
    textBeforeClosingFence.slice(openingFenceIndex + 3)
  );
  if (!codeText) return false;

  const openingFencePosition = positions[openingFenceIndex];
  if (openingFencePosition === undefined) return false;
  const codeBlockNode = createCodeBlockNode(
    editorState.schema,
    codeBlock,
    codeText
  );

  let transaction = editorState.tr.replaceRangeWith(
    openingFencePosition,
    from,
    codeBlockNode
  );
  const codeBlockPosition = findNodePositionAfter(
    transaction.doc,
    codeBlock,
    transaction.mapping.map(openingFencePosition, -1)
  );
  if (codeBlockPosition === null) return false;
  const paragraphPosition = codeBlockPosition + codeBlockNode.nodeSize;

  transaction = transaction
    .insert(paragraphPosition, paragraph.create())
    .setSelection(
      Selection.near(transaction.doc.resolve(paragraphPosition + 1))
    )
    .scrollIntoView();

  isApplyingFenceTransform = true;
  editorView.dispatch(transaction);
  isApplyingFenceTransform = false;
  return true;
};

const transformCompletedInlineCodeInEditor = () => {
  if (!editorView || isApplyingFenceTransform) return false;

  const { state: editorState } = editorView;
  const {
    schema: {
      marks: { code: codeMarkType },
      nodes: { code_block: codeBlock },
    },
    selection,
  } = editorState;

  if (!codeMarkType || selection.empty === false) return false;

  const { $from, from } = selection;
  if (codeBlock && $from.parent.type === codeBlock) return false;

  const { text: textBeforeCursor, positions } = textBeforePositionWithMap(
    editorState.doc,
    from
  );
  if (!textBeforeCursor.endsWith('`') || textBeforeCursor.endsWith('```')) {
    return false;
  }

  const textBeforeClosingTick = textBeforeCursor.slice(0, -1);
  const openingTickIndex = textBeforeClosingTick.lastIndexOf('`');
  if (openingTickIndex === -1) return false;

  const codeText = textBeforeClosingTick.slice(openingTickIndex + 1);
  if (!codeText || codeText.includes('\n')) return false;
  if (/^\s|\s$/.test(codeText)) return false;

  const openingTickPosition = positions[openingTickIndex];
  if (openingTickPosition === undefined) return false;

  const codeMark = codeMarkType.create();
  const trailingSpacePosition = openingTickPosition + codeText.length;
  const trailingSpaceNode = editorState.schema.text(' ');
  const cursorPosition = trailingSpacePosition + trailingSpaceNode.nodeSize;
  let transaction = editorState.tr
    .addMark(openingTickPosition + 1, from - 1, codeMark)
    .delete(from - 1, from)
    .delete(openingTickPosition, openingTickPosition + 1)
    .insert(trailingSpacePosition, trailingSpaceNode)
    .setStoredMarks(null);

  transaction = transaction
    .setSelection(selection.constructor.create(transaction.doc, cursorPosition))
    .scrollIntoView();

  isApplyingFenceTransform = true;
  editorView.dispatch(transaction);
  isApplyingFenceTransform = false;
  return true;
};

const toggleCodeBlock = () => {
  if (!editorView || !supportsCodeBlock.value || props.disabled) return;

  const {
    state: editorState,
    state: {
      schema: {
        nodes: { code_block: codeBlock, paragraph },
      },
    },
  } = editorView;

  if (isSelectionInsideCodeBlock() || editorState.selection.empty) {
    const nextNode = isSelectionInsideCodeBlock() ? paragraph : codeBlock;
    setBlockType(nextNode)(editorState, transaction => {
      editorView.dispatch(transaction.scrollIntoView());
    });
    editorView.focus();
    return;
  }

  const { from, to } = editorState.selection;
  const selectedText = selectedTextForCodeBlock(editorState.doc, from, to);
  const codeBlockNode = createCodeBlockNode(
    editorState.schema,
    codeBlock,
    selectedText
  );

  editorView.dispatch(
    editorState.tr.replaceSelectionWith(codeBlockNode, false).scrollIntoView()
  );
  editorView.focus();
};

const toggleBlockquote = () => {
  if (!editorView || !supportsBlockquote.value || props.disabled) return;

  const {
    state: editorState,
    state: {
      schema: {
        nodes: { blockquote },
      },
    },
  } = editorView;

  const command = isSelectionInsideBlockquote() ? lift : wrapIn(blockquote);
  command(editorState, transaction => {
    editorView.dispatch(transaction.scrollIntoView());
  });
  editorView.focus();
};

const removeChildTitles = button => {
  button.querySelectorAll('[title]').forEach(child => {
    child.removeAttribute('title');
    child.removeAttribute('data-original-title');
  });
};

const setButtonIcon = (button, iconClass) => {
  const icon = button.querySelector('.ProseMirror-icon') || button;
  const currentIcon = icon.firstElementChild;
  if (currentIcon?.dataset?.formatIcon === iconClass) return;

  icon.innerHTML = '';
  const iconElement = document.createElement('span');
  iconElement.className = `size-4 ${iconClass}`;
  iconElement.dataset.formatIcon = iconClass;
  iconElement.setAttribute('aria-hidden', 'true');
  icon.appendChild(iconElement);
};

const setAttributeIfChanged = (element, attribute, value) => {
  if (element.getAttribute(attribute) === value) return;
  element.setAttribute(attribute, value);
};

const findInlineCodeButton = menubar => {
  return Array.from(menubar.querySelectorAll('[title], [data-original-title]'))
    .filter(button => {
      const title = button.getAttribute('title');
      const originalTitle = button.getAttribute('data-original-title');
      return (
        title === INLINE_CODE_TITLE ||
        originalTitle === INLINE_CODE_TITLE ||
        title === codeInlineTooltip()
      );
    })
    .map(button => button.closest('.ProseMirror-menuitem') || button)
    .find(button => button.closest('.ProseMirror-menubar') === menubar);
};

const syncCodeFormattingButtons = () => {
  const menubar = editorRoot.value?.querySelector('.ProseMirror-menubar');
  if (!menubar) return;

  const inlineCodeButton = findInlineCodeButton(menubar);
  if (!inlineCodeButton) return;

  setAttributeIfChanged(inlineCodeButton, 'title', codeInlineTooltip());
  setAttributeIfChanged(inlineCodeButton, 'aria-label', codeInlineTooltip());
  inlineCodeButton.classList.add('ProseMirror-inline-code-button');
  removeChildTitles(inlineCodeButton);
  setButtonIcon(inlineCodeButton, 'i-lucide-code-xml');

  let codeBlockButton = menubar.querySelector(`.${CODE_BLOCK_BUTTON_CLASS}`);
  if (!supportsCodeBlock.value) {
    codeBlockButton?.remove();
    codeBlockButton = null;
  } else if (!codeBlockButton) {
    codeBlockButton = document.createElement('button');
    codeBlockButton.type = 'button';
    codeBlockButton.className = `ProseMirror-menuitem ProseMirror-icon ${CODE_BLOCK_BUTTON_CLASS}`;
    codeBlockButton.addEventListener('mousedown', event => {
      event.preventDefault();
    });
    codeBlockButton.addEventListener('click', event => {
      event.preventDefault();
      toggleCodeBlock();
    });
    inlineCodeButton.insertAdjacentElement('afterend', codeBlockButton);
  }

  if (codeBlockButton) {
    setAttributeIfChanged(codeBlockButton, 'title', codeBlockTooltip());
    setAttributeIfChanged(codeBlockButton, 'aria-label', codeBlockTooltip());
    removeChildTitles(codeBlockButton);
    codeBlockButton.classList.toggle(
      'ProseMirror-menu-active',
      isSelectionInsideCodeBlock()
    );
    setButtonIcon(codeBlockButton, 'i-lucide-square-code');
  }

  let blockquoteButton = menubar.querySelector(`.${BLOCKQUOTE_BUTTON_CLASS}`);
  if (!supportsBlockquote.value) {
    blockquoteButton?.remove();
    return;
  }

  if (!blockquoteButton) {
    blockquoteButton = document.createElement('button');
    blockquoteButton.type = 'button';
    blockquoteButton.className = `ProseMirror-menuitem ProseMirror-icon ${BLOCKQUOTE_BUTTON_CLASS}`;
    blockquoteButton.addEventListener('mousedown', event => {
      event.preventDefault();
    });
    blockquoteButton.addEventListener('click', event => {
      event.preventDefault();
      toggleBlockquote();
    });
    (codeBlockButton || inlineCodeButton).insertAdjacentElement(
      'afterend',
      blockquoteButton
    );
  }

  setAttributeIfChanged(blockquoteButton, 'title', blockquoteTooltip());
  setAttributeIfChanged(blockquoteButton, 'aria-label', blockquoteTooltip());
  removeChildTitles(blockquoteButton);
  blockquoteButton.classList.toggle(
    'ProseMirror-menu-active',
    isSelectionInsideBlockquote()
  );
  setButtonIcon(blockquoteButton, 'i-lucide-quote');
};

const translateMenuTooltips = () => {
  const menubar = editorRoot.value?.querySelector('.ProseMirror-menubar');
  if (!menubar) return;

  const translations = {
    'Toggle strong style': safeT('EDITOR.FORMAT_BAR.BOLD', 'Negrito'),
    'Toggle emphasis': safeT('EDITOR.FORMAT_BAR.ITALIC', 'Itálico'),
    'Toggle strikethrough': safeT('EDITOR.FORMAT_BAR.STRIKETHROUGH', 'Riscado'),
    [INLINE_CODE_TITLE]: codeInlineTooltip(),
    CODEBLOCK_BTN_PLACEHOLDER: codeBlockTooltip(),
    'Add or remove link': safeT('EDITOR.FORMAT_BAR.LINK', 'Link'),
    QUOTE_BTN_PLACEHOLDER: safeT('EDITOR.FORMAT_BAR.QUOTE', 'Citação'),
    'Wrap in bullet list': safeT(
      'EDITOR.FORMAT_BAR.BULLET_LIST',
      'Lista com marcadores'
    ),
    'Wrap in ordered list': safeT(
      'EDITOR.FORMAT_BAR.ORDERED_LIST',
      'Lista numerada'
    ),
    'Undo last change': safeT('EDITOR.FORMAT_BAR.UNDO', 'Desfazer'),
    'Redo last undone change': safeT('EDITOR.FORMAT_BAR.REDO', 'Refazer'),
  };

  const buttons = menubar.querySelectorAll('[title]');
  buttons.forEach(btn => {
    const title = btn.getAttribute('title');
    if (!title) return;

    if (!btn.hasAttribute('data-original-title')) {
      const parentTitle = btn.parentElement?.getAttribute(
        'data-original-title'
      );
      if (parentTitle) return; // Prevent nested spans duplicating tooltips
      btn.setAttribute('data-original-title', title);
    }

    // Some buttons might be wrapped, so we ensure the title matches what we track
    const origTitle = btn.getAttribute('data-original-title');
    if (
      origTitle &&
      translations[origTitle] &&
      title !== translations[origTitle]
    ) {
      btn.setAttribute('title', translations[origTitle]);
      // Remove title from children to prevent duplicate native tooltips floating around
      Array.from(btn.children).forEach(child => child.removeAttribute('title'));
    }
  });
  syncCodeFormattingButtons();
};

const syncLinkPrompt = () => {
  const dialog = document.querySelector('dialog.ProseMirror-prompt-backdrop');
  if (!dialog?.open) return;

  const title = dialog.querySelector('.sub-block-title');
  const input = dialog.querySelector('input[type="text"]');
  const submitButton = dialog.querySelector('button[type="submit"]');
  const cancelButton = dialog.querySelector('button[type="button"]');

  if (title) {
    title.textContent = safeT('EDITOR.LINK_PROMPT.TITLE', 'Create a link');
  }

  if (input) {
    const placeholder = safeT(
      'EDITOR.LINK_PROMPT.PLACEHOLDER',
      'https://example.com'
    );
    setAttributeIfChanged(input, 'placeholder', placeholder);
    setAttributeIfChanged(input, 'aria-label', placeholder);
  }

  if (submitButton) {
    submitButton.textContent = safeT(
      'EDITOR.LINK_PROMPT.SUBMIT',
      'Create link'
    );
  }

  if (cancelButton) {
    cancelButton.textContent = safeT('EDITOR.LINK_PROMPT.CANCEL', 'Cancel');
  }
};

const contentFromEditor = () => {
  return MessageMarkdownSerializer.serialize(editorView.state.doc);
};

const shouldShowVariables = computed(() => {
  return props.enableVariables && showVariables.value && !props.isPrivate;
});

const shouldShowCannedResponses = computed(() => {
  return (
    props.enableCannedResponses && showCannedMenu.value && !props.isPrivate
  );
});

const shouldShowMacros = computed(() => {
  return props.enableMacros && showMacroMenu.value;
});

const dismissMacros = () => {
  showMacroMenu.value = false;
  editorView?.focus();
};

const removeSuggestionTrigger = () => {
  if (!editorView || !range.value) return;

  const { from, to } = range.value;
  const end = Math.min(to, editorView.state.doc.content.size);
  editorView.dispatch(editorView.state.tr.delete(from, end));
  editorView.focus();
};

const onSelectMacro = macro => {
  removeSuggestionTrigger();
  emit('executeMacro', macro);
};

function createSuggestionPlugin({
  trigger,
  minChars = 0,
  showMenu,
  searchTerm,
  isAllowed = () => true,
}) {
  return suggestionsPlugin({
    matcher: triggerCharacters(trigger, minChars),
    suggestionClass: '',
    onEnter: args => {
      if (!isAllowed()) return false;
      showMenu.value = true;
      range.value = args.range;
      editorView = args.view;
      if (searchTerm) searchTerm.value = args.text || '';
      return false;
    },
    onChange: args => {
      editorView = args.view;
      range.value = args.range;
      if (searchTerm) searchTerm.value = args.text;
      return false;
    },
    onExit: () => {
      if (searchTerm) searchTerm.value = '';
      showMenu.value = false;
      return false;
    },
    onKeyDown: ({ event }) => {
      return event.keyCode === 13 && showMenu.value;
    },
  });
}

const plugins = computed(() => {
  if (!props.enableSuggestions) {
    return [];
  }

  return [
    createSuggestionPlugin({
      trigger: '@',
      showMenu: showUserMentions,
      searchTerm: mentionSearchKey,
      isAllowed: () => true,
    }),
    createSuggestionPlugin({
      trigger: '/',
      showMenu: showCannedMenu,
      searchTerm: cannedSearchTerm,
      isAllowed: () => !props.isPrivate,
    }),
    createSuggestionPlugin({
      trigger: '{{',
      showMenu: showVariables,
      searchTerm: variableSearchTerm,
      isAllowed: () => !props.isPrivate,
    }),
    createVariableInputRule({
      isPrivate: () => props.isPrivate,
      getVariables: () => props.variables,
    }),
    createSuggestionPlugin({
      trigger: '#',
      showMenu: showMacroMenu,
      searchTerm: macroSearchTerm,
      isAllowed: () => props.enableMacros,
    }),
    createSuggestionPlugin({
      trigger: ':',
      minChars: 2,
      showMenu: showEmojiMenu,
      searchTerm: emojiSearchTerm,
    }),
  ];
});

const sendWithSignature = computed(() => {
  // this is considered the source of truth, we watch this property
  // on change, we toggle the signature in the editor
  if (
    props.allowSignature &&
    !props.isPrivate &&
    props.channelType &&
    !props.disabled
  ) {
    return fetchSignatureFlagFromUISettings(props.channelType);
  }

  return false;
});

watch(showUserMentions, updatedValue => {
  emit('toggleUserMention', props.isPrivate && updatedValue);
});
watch(showCannedMenu, updatedValue => {
  emit('toggleCannedMenu', !props.isPrivate && updatedValue);
});
watch(showVariables, updatedValue => {
  emit('toggleVariablesMenu', !props.isPrivate && updatedValue);
});
watch(shouldShowMacros, updatedValue => {
  emit('toggleMacrosMenu', updatedValue);
});
function focusEditorInputField(pos = 'end') {
  const { tr } = editorView.state;

  const selection =
    pos === 'end' ? Selection.atEnd(tr.doc) : Selection.atStart(tr.doc);

  editorView.dispatch(tr.setSelection(selection));
  editorView.focus();
}

function isBodyEmpty(content) {
  // if content is undefined, we assume that the body is empty
  if (!content) return true;

  // Only strip the signature when it's actually being auto-appended for this
  // draft. Otherwise an agent whose typed text happens to match their saved
  // signature would be mistakenly treated as empty.
  const bodyWithoutSignature =
    sendWithSignature.value && props.signature
      ? removeSignatureHelper(
          content,
          props.signature,
          effectiveChannelType.value
        )
      : content;

  // trimming should remove all the whitespaces, so we can check the length
  return bodyWithoutSignature.trim().length === 0;
}

function handleEmptyBodyWithSignature() {
  const { schema, tr, doc } = state;

  const isEmptyParagraph = node =>
    node && node.type === schema.nodes.paragraph && node.content.size === 0;

  // Check if empty paragraph already exists to prevent duplicates when toggling signatures
  if (isEmptyParagraph(doc.firstChild)) {
    focusEditorInputField('start');
    return;
  }

  // create a paragraph node and
  // start a transaction to append it at the end
  const paragraph = schema.nodes.paragraph.create();
  const paragraphTransaction = tr.insert(0, paragraph);
  editorView.dispatch(paragraphTransaction);

  // Set the focus at the start of the input field
  focusEditorInputField('start');
}

function focusEditor(content) {
  if (props.disabled) return;

  const unrefContent = unref(content);
  if (isBodyEmpty(unrefContent) && sendWithSignature.value) {
    // reload state can be called when switching between conversations, or when drafts is loaded
    // these drafts can also have a signature, so we need to check if the body is empty
    // and handle things accordingly
    handleEmptyBodyWithSignature();
  } else if (props.focusOnMount) {
    // this is in the else block, handleEmptyBodyWithSignature also has a call to the focus method
    // the position is set to start, because the signature is added at the end of the body
    focusEditorInputField('end');
  }
}

function openFileBrowser() {
  imageUpload.value.click();
}

function reloadState(content = props.modelValue) {
  const unrefContent = unref(content);
  state = createState(
    unrefContent,
    props.placeholder,
    plugins.value,
    { onImageUpload: openFileBrowser },
    editorMenuOptions.value
  );

  editorView.updateState(state);
  focusEditor(unrefContent);
}

function addSignature() {
  if (props.disabled) return;
  let content = props.modelValue;
  // see if the content is empty, if it is before appending the signature
  // we need to add a paragraph node and move the cursor at the start of the editor
  const contentWasEmpty = isBodyEmpty(content);
  content = appendSignature(
    content,
    props.signature,
    effectiveChannelType.value
  );
  // need to reload first, ensuring that the editorView is updated
  reloadState(content);

  if (contentWasEmpty) {
    handleEmptyBodyWithSignature();
  }
}

function removeSignature() {
  if (props.disabled) return;
  if (!props.signature) return;
  let content = props.modelValue;
  content = removeSignatureHelper(
    content,
    props.signature,
    effectiveChannelType.value
  );
  // reload the state, ensuring that the editorView is updated
  reloadState(content);
}

function emitOnChange() {
  emit('input', contentFromEditor());
  emit('update:modelValue', contentFromEditor());
}

function toggleSourceMode() {
  if (isSourceMode.value) {
    // Switching from Source -> WYSIWYG: re-parse the markdown
    reloadState(sourceContent.value);
    emitOnChange();
    isSourceMode.value = false;
  } else {
    // Switching from WYSIWYG -> Source: serialize to markdown
    sourceContent.value = contentFromEditor();
    isSourceMode.value = true;
  }
}

function handleSourceInput(event) {
  sourceContent.value = event.target.value;
  emit('input', sourceContent.value);
  emit('update:modelValue', sourceContent.value);
}

function toggleSignatureInEditor(signatureEnabled) {
  // The toggleSignatureInEditor gets the new value from the
  // watcher, this means that if the value is true, the signature
  // is supposed to be added, else we remove it.
  if (signatureEnabled) {
    addSignature();
  } else {
    removeSignature();
  }
  // reloadState replaces editor state directly and bypasses dispatchTransaction,
  // so v-model never hears about the signature change — sync it back explicitly.
  emitOnChange();
}

function isEnterToSendEnabled() {
  return isEditorHotKeyEnabled('enter');
}

function isCmdPlusEnterToSendEnabled() {
  return isEditorHotKeyEnabled('cmd_enter');
}

function onImageInsertInEditor(fileUrl) {
  const { tr } = editorView.state;

  const insertData = findNodeToInsertImage(editorView.state, fileUrl);

  if (insertData) {
    editorView.dispatch(
      tr.insert(insertData.pos, insertData.node).scrollIntoView()
    );
    focusEditorInputField();
  }
}

async function uploadImageToStorage(file) {
  try {
    const { fileUrl } = await uploadFile(file);
    if (fileUrl) {
      onImageInsertInEditor(fileUrl);
    }
    useAlert(
      props.channelType === MESSAGE_SIGNATURE_FORMATTING
        ? t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SUCCESS'
          )
        : t('CONVERSATION.REPLYBOX.IMAGE_UPLOAD_SUCCESS')
    );
  } catch (error) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR')
    );
  }
}

function uploadImageIfWithinSizeLimit(file) {
  if (!file) return;
  if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    uploadImageToStorage(file);
  } else {
    useAlert(
      t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SIZE_ERROR',
        {
          size: MAXIMUM_FILE_UPLOAD_SIZE,
        }
      )
    );
  }
}

function onFileChange() {
  const input = imageUpload.value;
  uploadImageIfWithinSizeLimit(input.files[0]);
  input.value = '';
}

const allowsInlineImagePaste = computed(
  () =>
    !props.isPrivate &&
    (props.channelType === INBOX_TYPES.EMAIL ||
      props.channelType === INBOX_TYPES.WEB)
);

// Shift+Cmd/Ctrl+V on email/website: upload a clipboard image inline. This
// gesture's native paste event carries no image, so clipboard.read() is the
// only way to get the bytes. No preventDefault: text still pastes natively.
async function pasteInlineImageFromClipboard() {
  if (!editorView?.hasFocus()) return;
  if (!allowsInlineImagePaste.value || !navigator.clipboard?.read) return;
  try {
    const items = await navigator.clipboard.read();
    const imageItem = items.find(item =>
      item.types.some(type => INLINE_IMAGE_PASTE_TYPES.includes(type))
    );
    if (!imageItem) return;
    const imageType = imageItem.types.find(type =>
      INLINE_IMAGE_PASTE_TYPES.includes(type)
    );
    const blob = await imageItem.getType(imageType);
    uploadImageIfWithinSizeLimit(
      new File([blob], 'pasted-image', { type: imageType })
    );
  } catch (error) {
    // clipboard-read denied/unfocused (NotAllowedError): image can't be read.
    // Text paste is unaffected — ProseMirror handles it from the native event.
  }
}

useKeyboardEvents({
  'Alt+KeyP': {
    action: focusEditorInputField,
    allowOnFocusedInput: false,
  },
  'Alt+KeyL': {
    action: focusEditorInputField,
    allowOnFocusedInput: false,
  },
  '$mod+Shift+KeyV': {
    action: pasteInlineImageFromClipboard,
    allowOnFocusedInput: true,
  },
});

function handleLineBreakWhenEnterToSendEnabled(event) {
  if (
    hasPressedEnterAndNotCmdOrShift(event) &&
    isEnterToSendEnabled() &&
    !props.overrideLineBreaks
  ) {
    event.preventDefault();
  }
}

function insertLineBreakOnShiftEnter(event) {
  if (event.key !== 'Enter' || !event.shiftKey || !editorView) return false;

  const {
    state: editorState,
    state: {
      schema: {
        nodes: { code_block: codeBlock, hard_break: hardBreak },
      },
      selection,
    },
  } = editorView;

  if (!hardBreak) return false;
  if (codeBlock && selection.$from.parent.type === codeBlock) return false;

  event.preventDefault();
  editorView.dispatch(
    editorState.tr
      .replaceSelectionWith(hardBreak.create())
      .setStoredMarks(null)
      .scrollIntoView()
  );
  return true;
}

async function insertNodeIntoEditor(node, from = 0, to = 0) {
  state = insertAtCursor(editorView, node, from, to);
  emitOnChange();
  await nextTick();
  scrollCursorIntoView(editorView);
}

function insertContentIntoEditor(content, defaultFrom = 0) {
  const from = defaultFrom || editorView.state.selection.from || 0;
  // Use the editor's current schema to ensure compatibility with buildMessageSchema
  const currentSchema = editorView.state.schema;
  // Strip unsupported formatting before parsing to ensure content can be inserted
  // into channels that don't support certain markdown features (e.g., API channels)
  const sanitizedContent = stripUnsupportedFormatting(content, currentSchema);
  let node = new MessageMarkdownTransformer(currentSchema).parse(
    sanitizedContent
  );

  insertNodeIntoEditor(node, from, undefined);
}

/**
 * Inserts special content (mention, canned response, variable, emoji) into the editor.
 * @param {string} type - The type of special content to insert. Possible values: 'mention', 'canned_response', 'variable', 'emoji'.
 * @param {Object|string} content - The content to insert, depending on the type.
 */
function insertSpecialContent(type, content) {
  if (!editorView) {
    return;
  }

  let { node, from, to } = getContentNode(
    editorView,
    type,
    content,
    range.value,
    props.variables
  );

  if (!node) return;

  insertNodeIntoEditor(node, from, to);

  const event_map = {
    mention: CONVERSATION_EVENTS.USED_MENTIONS,
    cannedResponse: CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE,
    variable: CONVERSATION_EVENTS.INSERTED_A_VARIABLE,
    emoji: CONVERSATION_EVENTS.INSERTED_AN_EMOJI,
    tool: CONVERSATION_EVENTS.INSERTED_A_TOOL,
  };

  useTrack(event_map[type]);
}

function handleCannedResponseReplace(item) {
  const content = typeof item === 'object' ? item.content : item;
  const attachments = typeof item === 'object' ? item.attachments : [];

  insertSpecialContent('cannedResponse', content);

  if (attachments && attachments.length > 0) {
    emit('insertCannedResponse', { attachments });
  }
}

function handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
  if (
    hasPressedCommandAndEnter(event) &&
    isCmdPlusEnterToSendEnabled() &&
    !props.overrideLineBreaks
  ) {
    event.preventDefault();
  }
}

function onKeydown(event) {
  if (isComposing(event)) return false;

  if (insertPendingFenceLineBreak(event)) {
    return true;
  }
  if (insertLineBreakOnShiftEnter(event)) {
    return true;
  }
  if (isEscape(event)) {
    collapseSelection(editorView);
    return true;
  }
  if (isEnterToSendEnabled()) {
    handleLineBreakWhenEnterToSendEnabled(event);
  }
  if (isCmdPlusEnterToSendEnabled()) {
    handleLineBreakWhenCmdAndEnterToSendEnabled(event);
  }
  return false;
}

function createEditorView() {
  editorView = new EditorView(editor.value, {
    state: state,
    editable: () => !props.disabled,
    nodeViews: {
      image: imageResizeView,
    },
    dispatchTransaction: tx => {
      state = state.apply(tx);
      editorView.updateState(state);
      if (tx.docChanged) {
        emitOnChange();
      }
      syncCodeFormattingButtons();
      if (tx.docChanged) {
        if (!transformCompletedFenceInEditor()) {
          transformCompletedInlineCodeInEditor();
        }
      }
    },
    handleDOMEvents: {
      keyup: () => {
        if (!props.disabled) {
          typingIndicator.start();
        }
      },
      beforeinput: (view, event) =>
        !props.disabled && handlePendingFenceTextInput(event),
      keydown: (view, event) => !props.disabled && onKeydown(event),
      focus: () => !props.disabled && emit('focus'),
      blur: () => {
        if (props.disabled) return;
        typingIndicator.stop();
        emit('blur');
      },
      paste: (view, event) => {
        if (props.disabled) return;
        const { files } = event.clipboardData;
        if (!files.length) return;
        event.preventDefault();
        // Paste text content alongside files (e.g., spreadsheet data from Numbers app)
        // Numbers app includes invalid 0-byte attachments with text, so we paste the text here
        // while ReplyBox filters and handles valid file attachments
        const text = event.clipboardData.getData('text/plain');
        if (text) {
          view.dispatch(view.state.tr.insertText(text));
          emitOnChange();
        }
      },
    },
  });
}

watch(
  computed(() => props.modelValue),
  (newVal = '') => {
    if (newVal !== contentFromEditor()) {
      reloadState(newVal);
    }
  }
);

watch(
  computed(() => props.editorId),
  () => {
    showCannedMenu.value = false;
    showEmojiMenu.value = false;
    showVariables.value = false;
    showMacroMenu.value = false;
    cannedSearchTerm.value = '';
    macroSearchTerm.value = '';
    reloadState(props.modelValue);
  }
);

watch(
  computed(() => props.isPrivate),
  () => {
    reloadState(props.modelValue);
  }
);

watch(effectiveChannelType, () => {
  reloadState(props.modelValue);
});

watch(
  computed(() => props.disabled),
  () => editorView?.setProps({})
);

watch(
  computed(() => props.updateSelectionWith),
  (newValue, oldValue) => {
    if (!editorView) return;

    if (newValue !== oldValue) {
      if (props.updateSelectionWith !== '') {
        const node = editorView.state.schema.text(props.updateSelectionWith);

        const tr = editorView.state.tr.replaceSelectionWith(node);
        editorView.focus();
        state = editorView.state.apply(tr);
        editorView.updateState(state);
        emitOnChange();
        emit('clearSelection');
      }
    }
  }
);

watch(sendWithSignature, newValue => {
  // see if the allowSignature flag is true
  if (props.allowSignature && !props.disabled) {
    toggleSignatureInEditor(newValue);
  }
});

onMounted(() => {
  // [VITE] state assignment was done in created before
  state = createState(
    props.modelValue,
    props.placeholder,
    plugins.value,
    { onImageUpload: openFileBrowser },
    editorMenuOptions.value
  );

  createEditorView();
  editorView.updateState(state);
  if (props.focusOnMount) {
    focusEditorInputField();
  }

  menuObserver = new MutationObserver(() => {
    translateMenuTooltips();
  });
  if (editorRoot.value) {
    menuObserver.observe(editorRoot.value, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['title'],
    });
    translateMenuTooltips();
  }

  promptObserver = new MutationObserver(() => {
    syncLinkPrompt();
  });
  promptObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

onUnmounted(() => {
  if (menuObserver) {
    menuObserver.disconnect();
  }
  if (promptObserver) {
    promptObserver.disconnect();
  }
});

defineExpose({ focusEditorInputField });

// BUS Event to insert text or markdown into the editor at the
// current cursor position.
// Components using this
// 1. SearchPopover.vue
useEmitter(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content => {
  if (!props.enableInsertEvents) return;
  insertContentIntoEditor(content);
});
</script>

<template>
  <div
    ref="editorRoot"
    class="relative w-full"
    :class="{
      'opacity-50 cursor-not-allowed pointer-events-none': disabled,
    }"
  >
    <TagAgents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @select-agent="content => insertSpecialContent('mention', content)"
    />
    <CannedResponse
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @replace="handleCannedResponseReplace"
    />
    <VariableList
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @select-variable="content => insertSpecialContent('variable', content)"
    />
    <MacroList
      v-if="shouldShowMacros"
      :search-key="macroSearchTerm"
      @close="dismissMacros"
      @remove-trigger="removeSuggestionTrigger"
      @select-macro="onSelectMacro"
    />
    <KeyboardEmojiSelector
      v-if="showEmojiMenu"
      :search-key="emojiSearchTerm"
      @select-emoji="emoji => insertSpecialContent('emoji', emoji)"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onFileChange"
    />
    <div v-show="!isSourceMode" ref="editor" class="editor-mount" />
    <textarea
      v-if="isSourceMode"
      :value="sourceContent"
      class="source-mode-textarea"
      :placeholder="placeholder"
      @input="handleSourceInput"
    />
    <button
      v-if="showSourceToggle"
      v-tooltip.top="
        isSourceMode
          ? $t('EDITOR.SOURCE_MODE_OFF')
          : $t('EDITOR.SOURCE_MODE_ON')
      "
      type="button"
      class="pointer-events-auto absolute right-3 top-1 z-[60] flex size-8 cursor-pointer items-center justify-center rounded text-n-slate-10 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
      :aria-label="
        isSourceMode
          ? $t('EDITOR.SOURCE_MODE_OFF')
          : $t('EDITOR.SOURCE_MODE_ON')
      "
      @mousedown.prevent
      @click="toggleSourceMode"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="pointer-events-none block size-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline v-if="!isSourceMode" points="16 18 22 12 16 6" />
        <polyline v-if="!isSourceMode" points="8 6 2 12 8 18" />
        <path
          v-if="isSourceMode"
          d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"
        />
      </svg>
    </button>
    <slot name="footer" />
  </div>
</template>

<style lang="scss">
@import '@chatwoot/prosemirror-schema/src/styles/base.scss';

.ProseMirror-menubar-wrapper {
  @apply flex flex-col gap-3;

  .ProseMirror-menubar {
    min-height: 1.25rem !important;
    @apply items-center gap-4 flex pb-0 bg-transparent text-n-slate-11 relative ltr:-left-[3px] rtl:-right-[3px];

    .ProseMirror-menu-active {
      @apply bg-n-slate-5 dark:bg-n-solid-3 !important;
    }

    .ProseMirror-menuitem {
      @apply mr-0 size-4 flex items-center justify-center;

      .ProseMirror-icon {
        @apply size-4 flex items-center justify-center flex-shrink-0;

        svg {
          @apply size-full;
        }
      }
    }

    .ProseMirror-inline-code-button,
    .ProseMirror-code-block-button,
    .ProseMirror-blockquote-button {
      @apply rounded-[4px] text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-n-solid-3;
    }

    .ProseMirror-code-block-button,
    .ProseMirror-blockquote-button {
      @apply size-4;
    }
  }

  .ProseMirror-menubar:not(:has(*)) {
    max-height: none !important;
    min-height: 0 !important;
    padding: 0 !important;
    display: none !important;
  }

  > .ProseMirror {
    @apply p-0 break-words text-n-slate-12;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-n-slate-12;
    }

    blockquote {
      @apply border-n-slate-7;

      p {
        @apply text-n-slate-11;
      }
    }
  }
}

// Room for the table grips and add buttons, which sit outside the table.
// .editor-mount keeps this off the article editor.
.editor-mount .ProseMirror .tableWrapper {
  @apply m-0 pt-4 pb-5 ps-5 pe-6;
}

.ProseMirror-woot-style {
  @apply overflow-auto;
}

.ProseMirror-woot-style:not(
    :where(.resizable-editor-wrapper .ProseMirror-woot-style)
  ) {
  @apply min-h-[5rem] max-h-[7.5rem];
}

// Resizable editor wrapper styles
.resizable-editor-wrapper {
  .ProseMirror-woot-style {
    min-height: clamp(
      var(--editor-min-allowed, var(--editor-min-height, 5rem)),
      var(--editor-height, var(--editor-min-height, 5rem)),
      var(--editor-max-allowed, var(--editor-max-height, 7.5rem))
    );
    max-height: clamp(
      var(--editor-min-allowed, var(--editor-min-height, 5rem)),
      var(--editor-height, var(--editor-min-height, 5rem)),
      var(--editor-max-allowed, var(--editor-max-height, 7.5rem))
    );
    transition:
      min-height var(--editor-height-transition, 180ms ease),
      max-height var(--editor-height-transition, 180ms ease);
  }
}

dialog.ProseMirror-prompt-backdrop {
  @apply fixed inset-0 m-0 flex h-dvh max-h-none w-dvw max-w-none items-center justify-center border-0 bg-transparent p-4;
}

dialog.ProseMirror-prompt-backdrop::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

dialog.ProseMirror-prompt-backdrop .ProseMirror-prompt {
  @apply w-full max-w-sm rounded-lg border border-n-weak bg-n-solid-3 p-4 shadow-xl;

  form {
    @apply m-0;
  }

  .sub-block-title {
    @apply mb-3 text-sm font-semibold text-n-slate-12;
  }

  input[type='text'] {
    @apply mb-3 w-full rounded-md border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-colors placeholder:text-n-slate-10 focus:outline-n-brand;
  }

  .ProseMirror-prompt-buttons {
    @apply mt-2 flex justify-end gap-2;

    button {
      @apply h-8 cursor-pointer rounded-md border-0 px-3 text-sm font-medium shadow-none transition-colors;

      &[type='submit'] {
        @apply bg-n-brand text-white hover:bg-n-brand/90;
      }

      &[type='button'] {
        @apply bg-n-alpha-black2 text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12;
      }
    }
  }
}

.is-private {
  .prosemirror-mention-node {
    @apply font-medium bg-n-amber-2/80 dark:bg-n-amber-2/80 text-n-slate-12 py-0 px-1;
  }

  .ProseMirror-menubar-wrapper {
    > .ProseMirror {
      @apply text-n-slate-12;

      p {
        @apply text-n-slate-12;
      }
    }
  }
}

.prosemirror-tools-node {
  @apply font-medium text-n-slate-12 py-0;
}

.editor-wrap {
  @apply mb-4;
}

.message-editor {
  @apply rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 bg-n-alpha-black2 py-0 px-1 mb-0;
}

.editor_warning {
  @apply outline outline-1 outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9;
}

.editor-warning__message {
  @apply text-n-ruby-9 dark:text-n-ruby-9 font-normal text-sm pt-1 pb-0 px-0;
}

// Float editor menu
.popover-prosemirror-menu {
  position: relative;

  .ProseMirror p:last-child:not(:is(th, td) > p) {
    margin-bottom: 10px !important;
  }

  .ProseMirror-menubar {
    display: none; // Hide by default
  }

  &.show-formatting-bar {
    .ProseMirror-menubar {
      display: flex !important;
      position: relative !important;
      min-height: 1.25rem !important;
      @apply rounded-lg !py-1 z-50 bg-transparent items-center gap-4 ml-0 mb-3 shadow-none outline-none text-n-slate-11 w-full flex-wrap;

      .ProseMirror-menuitem {
        @apply mr-0 size-4 flex items-center;

        .ProseMirror-icon {
          @apply p-0.5 flex-shrink-0;
        }
      }
    }

    .ProseMirror-menubar:not(:has(*)) {
      display: none !important;
    }
  }
}

.source-mode-textarea {
  @apply w-full min-h-[7.5rem] max-h-[15rem] p-3 rounded-lg font-mono text-sm text-n-slate-12 bg-n-alpha-black2 outline outline-1 outline-n-weak resize-y border-none;

  &:focus {
    @apply outline-n-brand;
  }
}

.ProseMirror {
  ul li p,
  ol li p {
    margin: 0;
  }
}
</style>
